// The single client owner of an execution item's effective execution profile
// (blueprint §12.1, §15.8). The decision is keyed strictly on `origin` and
// mirrors the backend `profileForItem` in executionDocument.js:
// - PRESCRIBED → the immutable prescription-snapshot profile of its
//   prescribedExerciseKey. A profile copied onto the local item is never
//   authority;
// - ADDED / SUBSTITUTED → the item's frozen executionProfile or, for a legacy
//   row accepted before profiles were stored, its stored core with unknown
//   hints. Never the snapshot of the answered slot, never the live catalog.
// Anything else (an unmapped slot, an unknown origin) has no effective
// profile: null means the item cannot be logged.

const ITEM_OWNED_ORIGINS = new Set(["ADDED", "SUBSTITUTED"]);

function originOf(item) {
  return String(item?.origin || "").toUpperCase();
}

export function prescribedExerciseForItem(record, item) {
  if (item?.prescribedExerciseKey == null) return null;
  for (const block of record?.server?.snapshot?.blocks || []) {
    for (const exercise of block.exercises || []) {
      if (exercise.exerciseKey === item.prescribedExerciseKey) return exercise;
    }
  }
  return null;
}

function hasCore(profile) {
  return Boolean(profile?.measurementType && profile?.loadMode);
}

// The canonical legacy fallback of an item-owned row whose profile was never
// frozen (§12.1): its stored core columns, with hints left unknown.
export function legacyItemExecutionProfile(item) {
  if (!hasCore(item)) return null;
  return {
    version: 1,
    measurementType: item.measurementType,
    loadMode: item.loadMode,
    suggestedFamily: item.suggestedFamily || null,
    entryHint: null,
    repsPerSide: false,
    notesHint: null,
  };
}

// The frozen profile an item-owned row carries, or its legacy fallback.
export function itemOwnedExecutionProfile(item) {
  return hasCore(item?.executionProfile)
    ? item.executionProfile
    : legacyItemExecutionProfile(item);
}

export function effectiveExecutionProfile(record, item) {
  const origin = originOf(item);
  if (origin === "PRESCRIBED") {
    const profile = prescribedExerciseForItem(record, item)?.profile;
    return hasCore(profile) ? profile : null;
  }
  if (ITEM_OWNED_ORIGINS.has(origin)) return itemOwnedExecutionProfile(item);
  return null;
}

// Prescription ghosts come from the immutable snapshot, but a prescribed value
// is shown only when that value stays meaningful for the item's effective
// profile (§15.8). Each ghost field belongs to one measurement type and is
// kept only when both the prescribed slot and the effective profile have it.
// The V1 snapshot carries no prescribed load, so no load ghost exists; none is
// converted.
const SET_GHOST_MEASUREMENT = Object.freeze({
  targetReps: "REPS",
  minReps: "REPS",
  maxReps: "REPS",
  targetRir: "REPS",
  targetSeconds: "TIMED_HOLD",
});

function ghostMeasurements(record, item) {
  return {
    effective: effectiveExecutionProfile(record, item)?.measurementType || null,
    prescribed: prescribedExerciseForItem(record, item)?.profile?.measurementType || null,
  };
}

function ghostCompatible({ effective, prescribed }, measurementType) {
  return effective === measurementType && prescribed === measurementType;
}

export function compatiblePrescribedSet(record, item, prescribedSet) {
  if (!prescribedSet) return prescribedSet;
  const measurements = ghostMeasurements(record, item);
  const compatible = { ...prescribedSet };
  Object.entries(SET_GHOST_MEASUREMENT).forEach(([field, measurementType]) => {
    if (!ghostCompatible(measurements, measurementType)) compatible[field] = null;
  });
  return compatible;
}

// The exercise's prescribed tempo, formatted for display ("3-1-1-0"), or null.
// Tempo belongs to the exercise, never to a set: the slot's defaultTempo, else
// the first set-level value the snapshot still carries for it (the same
// precedence the Weekly Plan viewer uses). It is an instruction for the planned
// exercise only: a substitute never shows it, and the slot shows it again once
// it holds its planned exercise.
export function prescribedTempoForItem(record, item) {
  if (item?.origin !== "PRESCRIBED") return null;
  if (!ghostCompatible(ghostMeasurements(record, item), "REPS")) return null;
  const prescribed = prescribedExerciseForItem(record, item);
  const raw = prescribed?.defaultTempo ||
    (prescribed?.sets || []).find((set) => set.tempo)?.tempo;
  return formatCanonicalTempo(raw);
}

// The canonical tempo is exactly four digits ("3110"), shown as "3-1-1-0".
// Anything else is not a tempo: it is never repaired or completed here.
export function formatCanonicalTempo(value) {
  const tempo = typeof value === "string" ? value.trim() : "";
  return /^\d{4}$/.test(tempo) ? tempo.split("").join("-") : null;
}

// The planned exercise note: the short coaching instruction the plan attaches
// to its exercise ("Pause at peak contraction."). `notes` is the canonical
// snapshot field; `executionNotes` is its legacy mirror. Like the tempo it is
// an instruction for the planned exercise only: a substitute never shows it,
// and the slot shows it again once it holds its planned exercise.
export function plannedNoteForItem(record, item) {
  if (item?.origin !== "PRESCRIBED") return null;
  const prescribed = prescribedExerciseForItem(record, item);
  // A cardio prescription carries its own instruction (`cardio.notes`, absent
  // from snapshots taken before it was kept).
  const note = String(
    prescribed?.notes || prescribed?.executionNotes || prescribed?.cardio?.notes || ""
  ).trim();
  return note || null;
}

// The prescribed Cardio minutes ghost, including the "Done accepts the ghost"
// completion (§12.6), only for a Cardio item answering a Cardio prescription.
export function compatibleCardioGhostMinutes(record, item) {
  if (!ghostCompatible(ghostMeasurements(record, item), "CARDIO_MINUTES")) return null;
  return prescribedExerciseForItem(record, item)?.cardio?.durationMinutes ?? null;
}
